import { QK } from '@/js/consts/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
    getRoleSharesForDashboard,
    getUserSharesForDashboard,
    putShareDashboardToRoles,
    putShareDashboardToUsers,
} from './shareDashboardApi';
import { UserShare } from './types';
import type { Ref } from 'vue';
import { SAVE_INDICATOR_MIN_MS, withMinDuration } from '@/js/utils/withMinDuration';

export const useGetUserSharesForDashboard = (dashboardId: string, enabled?: Ref<boolean>) => {
    return useQuery({
        queryKey: [QK.SHARE_DASHBOARD.USERS, dashboardId],
        queryFn: () => getUserSharesForDashboard(dashboardId),
        enabled,
    });
};

export const usePutShareDashboardToUsers = (dashboardId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (users: Pick<UserShare, 'id' | 'accessLevel'>[]) => {
            const result = await withMinDuration(
                putShareDashboardToUsers({ dashboardId, users }),
                SAVE_INDICATOR_MIN_MS
            );
            await queryClient.invalidateQueries({
                queryKey: [QK.SHARE_DASHBOARD.USERS, dashboardId],
            });
            return result;
        },
    });
};

export const useGetRoleSharesForDashboard = (dashboardId: string, enabled?: Ref<boolean>) => {
    return useQuery({
        queryKey: [QK.SHARE_DASHBOARD.ROLES, dashboardId],
        queryFn: () => getRoleSharesForDashboard(dashboardId),
        enabled,
    });
};

export const usePutShareDashboardToRoles = (dashboardId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (roleIds: string[]) => {
            const result = await withMinDuration(
                putShareDashboardToRoles({ dashboardId, roleIds }),
                SAVE_INDICATOR_MIN_MS
            );
            await queryClient.invalidateQueries({
                queryKey: [QK.SHARE_DASHBOARD.ROLES, dashboardId],
            });
            return result;
        },
    });
};
